// AuthModal.tsx - Compass.com style login/signup modal
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Stack,
  Box,
  Divider,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth0User } from '../context/Auth0UserContext';

interface AuthModalProps {
  open: boolean;
  onClose: (successfulLogin?: boolean) => void;
  mode: 'signin' | 'signup';
  onModeSwitch?: () => void;
  isRequestingContext?: boolean; // Indicates if modal opened from request flow
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, mode, onModeSwitch, isRequestingContext = false }) => {
  const { loginWithSocial, loginWithEmailOTP, loading, setIsRequestingToSpot } = useAuth0User();
  
  // Ensure isRequestingToSpot flag is set when modal opens in request context
  React.useEffect(() => {
    if (open && isRequestingContext) {
      setIsRequestingToSpot(true);
    }
  }, [open, isRequestingContext, setIsRequestingToSpot]);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showOTPForm, setShowOTPForm] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOTP] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSocialLogin = (provider: string) => {
    // Pass isSignIn based on the modal's current mode
    // Both signin and signup should go to dashboard after OAuth
    loginWithSocial(provider, mode === 'signin');
  };

  const handleEmailClick = () => {
    setShowEmailForm(true);
    setError('');
  };

  const handleClose = (successfulLogin: boolean = false) => {
    // Reset all form state when closing
    setShowEmailForm(false);
    setShowOTPForm(false);
    setEmail('');
    setOTP('');
    setError('');
    setIsLoading(false);
    onClose(successfulLogin);
  };

  const handleBack = () => {
    if (showOTPForm) {
      setShowOTPForm(false);
      setShowEmailForm(true);
      setOTP('');
      setError('');
    } else if (showEmailForm) {
      setShowEmailForm(false);
      setEmail('');
      setError('');
    } else {
      handleClose();
    }
  };

  const handleEmailSubmit = async () => {
    if (!email) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await loginWithEmailOTP(email);
      if (result.success) {
        setShowEmailForm(false);
        setShowOTPForm(true);
      } else {
        setError(result.error || 'Failed to send code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async () => {
    if (!otp || !email) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await loginWithEmailOTP(email, otp);
      if (result.success) {
        // Success! Close modal with success flag to trigger redirect
        handleClose(true);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      {/* Header with back button and close */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <IconButton onClick={() => handleClose(false)} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, py: 3 }}>
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            mb: 1,
            color: '#1a1a1a',
          }}
        >
          {mode === 'signup' ? 'Create Account' : 'Sign In'}
        </Typography>

        {/* Explanatory text */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mb: 3,
            color: '#666',
            px: 1,
          }}
        >
          {mode === 'signup' 
            ? '🎥 Your video and message are saved! Choose how to sign up:'
            : 'Choose how to sign in to continue:'
          }
        </Typography>

        <Stack spacing={2}>
          {!showEmailForm && !showOTPForm && (
            <>
              {/* Google Button */}
              <Button
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
                startIcon={<GoogleIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  bgcolor: '#fff',
                  color: '#1a1a1a',
                  border: '1px solid #dadce0',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: '#f8f9fa',
                    borderColor: '#d2d3d4',
                  },
                }}
              >
                Continue with Google
              </Button>

              {/* Facebook Button */}
              <Button
                onClick={() => handleSocialLogin('facebook')}
                disabled={loading}
                startIcon={<FacebookIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  bgcolor: '#1877F2',
                  color: '#fff',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: '#166fe5',
                  },
                }}
              >
                Continue with Facebook
              </Button>

              {/* Email Button */}
              <Button
                onClick={handleEmailClick}
                disabled={loading}
                startIcon={<EmailIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  bgcolor: '#fff',
                  color: '#1a1a1a',
                  border: '1px solid #dadce0',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: '#f8f9fa',
                    borderColor: '#d2d3d4',
                  },
                }}
              >
                Continue with Email
              </Button>
            </>
          )}

          {showEmailForm && !showOTPForm && (
            <>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, textAlign: 'center' }}>
                We'll send a verification code to your email
              </Typography>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoFocus
                error={!!error}
                helperText={error}
                disabled={isLoading}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                sx={{ mt: 1 }}
              />
              <Button
                onClick={handleEmailSubmit}
                disabled={isLoading || !email}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  bgcolor: '#2D2070',
                  color: '#fff',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: '#1f1750',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#2D2070',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
              </Button>
            </>
          )}

          {showOTPForm && (
            <>
              <Typography variant="body2" sx={{ color: '#666', mb: 1, textAlign: 'center' }}>
                Enter the 6-digit code sent to {email}
              </Typography>
              <TextField
                label="Verification Code"
                value={otp}
                onChange={(e) => setOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                autoFocus
                error={!!error}
                helperText={error}
                disabled={isLoading}
                onKeyDown={(e) => e.key === 'Enter' && handleOTPSubmit()}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' } }}
              />
              <Button
                onClick={handleOTPSubmit}
                disabled={isLoading || otp.length !== 6}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  bgcolor: '#2D2070',
                  color: '#fff',
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: '#1f1750',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#2D2070',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : otp.length !== 6 ? (
                  'Enter code...'
                ) : mode === 'signup' ? (
                  'Verify & Create Account'
                ) : (
                  'Verify & Sign In'
                )}
              </Button>
            </>
          )}
        </Stack>

        {/* Info box for signup mode */}
        {mode === 'signup' && !showEmailForm && !showOTPForm && (
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: '#f0f7ff', 
            borderRadius: 2,
            border: '1px solid #c5e1ff'
          }}>
            <Typography variant="caption" sx={{ color: '#2D2070', display: 'block', mb: 0.5, fontWeight: 600 }}>
              📝 What happens next?
            </Typography>
            <Typography variant="caption" sx={{ color: '#555', fontSize: '11px' }}>
              After authentication, you'll complete your profile with username and bio. 
              Then you'll return right here to submit your request! ✨
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption" sx={{ color: '#999', fontSize: '12px' }}>
            By continuing, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#999', textDecoration: 'underline' }}>Terms of Service</a>
            {' & '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#999', textDecoration: 'underline' }}>Privacy Policy</a>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
